import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, API } from "@/lib/api";
import { toast } from "sonner";

const THEMES = [
  { code: "rose_elegant", name: "Rose Elegant" },
  { code: "ivory_minimal", name: "Ivory Minimal" },
  { code: "luxury_gold", name: "Luxury Gold" },
  { code: "olive_garden", name: "Olive Garden" },
  { code: "night_premium", name: "Night Premium" },
];

const TABS = [
  "Genel Bilgiler","Tasarım","Fotoğraflar","Hikâyemiz","Program","LCV","Müzik","Misafir Anıları","QR Kod"
];

function slugify(s) {
  const tr = {"ç":"c","ğ":"g","ı":"i","ö":"o","ş":"s","ü":"u","Ç":"c","Ğ":"g","İ":"i","Ö":"o","Ş":"s","Ü":"u"};
  return s.toLowerCase().replace(/[çğıöşüÇĞİÖŞÜ]/g, m => tr[m] || m).replace(/[^a-z0-9\s-]/g,"").replace(/\s+/g,"-").replace(/-+/g,"-").replace(/^-|-$/g,"");
}

const empty = {
  slug: "", bride_name: "", groom_name: "", wedding_date: "", wedding_time: "19:30",
  venue: "", address: "", maps_url: "", cover_image: "", hero_video: "", hero_video_url: "", hero_video_poster: "", hero_video_enabled: false, overlay_opacity: 0.4,
  invitation_message: "Düğünümüze Davetlisiniz", intro_text: "", music_url: "",
  theme: "rose_elegant", gallery: [], program: [], story: [], published: true, rsvp_visible: true, program_note: "",
};

export default function WeddingEditor() {
  const { id } = useParams();
  const isNew = !id;
  const nav = useNavigate();
  const [tab, setTab] = useState(0);
  const [w, setW] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState("desktop");
  const [rsvps, setRsvps] = useState({ items: [], stats: {} });
  const [rsvpFilter, setRsvpFilter] = useState("all");
  const [rsvpSearch, setRsvpSearch] = useState("");
  const [memories, setMemories] = useState([]);
  const [memFilter, setMemFilter] = useState("pending");

  useEffect(() => {
    if (isNew) return;
    (async () => {
      try {
        const { data } = await api.get(`/weddings/${id}`);
        setW({ ...empty, ...data });
      } catch { toast.error("Yüklenemedi"); }
    })();
  }, [id, isNew]);

  const loadRsvps = async () => {
    if (isNew) return;
    const { data } = await api.get(`/weddings/${id}/rsvps`);
    setRsvps(data);
  };
  const loadMemories = async () => {
    if (isNew) return;
    const { data } = await api.get(`/weddings/${id}/memories`, { params: memFilter !== "all" ? { status: memFilter } : {} });
    setMemories(data);
  };
  useEffect(() => { if (tab === 5) loadRsvps(); if (tab === 7) loadMemories(); }, [tab, memFilter]);

  const set = (k, v) => setW(prev => ({ ...prev, [k]: v }));
  const autoslug = () => set("slug", slugify(`${w.bride_name}-${w.groom_name}`));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...w, slug: w.slug || slugify(`${w.bride_name}-${w.groom_name}`) };
      if (isNew) {
        const { data } = await api.post("/weddings", payload);
        toast.success("Oluşturuldu");
        nav(`/admin/wedding/${data.id}`);
      } else {
        await api.put(`/weddings/${id}`, payload);
        toast.success("Kaydedildi");
      }
    } catch (e) {
      toast.error("Kaydedilemedi");
    } finally { setSaving(false); }
  };

  const uploadFile = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
  };

  const addGalleryUrl = () => {
    const url = window.prompt("Fotoğraf URL");
    if (!url) return;
    set("gallery", [...w.gallery, { id: crypto.randomUUID(), url, caption: null }]);
  };
  const addGalleryFile = async (file) => {
    const url = await uploadFile(file);
    set("gallery", [...w.gallery, { id: crypto.randomUUID(), url, caption: null }]);
  };

  const filteredRsvps = useMemo(() => {
    let items = rsvps.items || [];
    if (rsvpFilter === "yes") items = items.filter(r => r.attending);
    if (rsvpFilter === "no") items = items.filter(r => !r.attending);
    if (rsvpSearch) items = items.filter(r => r.name.toLowerCase().includes(rsvpSearch.toLowerCase()));
    return items;
  }, [rsvps, rsvpFilter, rsvpSearch]);

  const exportCsv = () => {
    const token = localStorage.getItem("bigeldi_token");
    window.open(`${API}/weddings/${id}/rsvps/export?auth=${token}`, "_blank");
  };

  const publicUrl = w.slug ? `${window.location.origin}/dugun/${w.slug}` : "";
  const qrUrl = publicUrl ? `https://api.qrserver.com/v1/create-qr-code/?size=400x400&data=${encodeURIComponent(publicUrl)}` : "";
  const qrSvg = publicUrl ? `https://api.qrserver.com/v1/create-qr-code/?format=svg&size=400x400&data=${encodeURIComponent(publicUrl)}` : "";

  return (
    <div className="min-h-screen bg-brand">
      <header className="border-b border-brand bg-surface-brand sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin" className="text-secondary-brand hover:text-accent-brand text-xs uppercase tracking-luxury">← Panel</Link>
            <div className="font-serif-elegant text-xl">{isNew ? "Yeni Düğün" : `${w.bride_name} & ${w.groom_name}`}</div>
          </div>
          <div className="flex items-center gap-3">
            {w.slug && <Link to={`/dugun/${w.slug}`} target="_blank" className="btn-ghost" data-testid="editor-preview-btn">Önizle</Link>}
            <button onClick={save} disabled={saving} className="btn-primary" data-testid="editor-save">{saving?"Kaydediliyor...":"Kaydet"}</button>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-6 flex gap-1 overflow-x-auto no-scrollbar">
          {TABS.map((t, i) => (
            <button key={t} onClick={()=>setTab(i)}
              className={`px-4 py-3 text-xs uppercase tracking-luxury whitespace-nowrap border-b-2 transition ${tab===i ? "text-accent-brand" : "text-secondary-brand border-transparent hover:text-primary-brand"}`}
              style={tab===i ? {borderColor:"var(--accent)"} : {}}
              data-testid={`tab-${i}`}>
              {t}
            </button>
          ))}
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-6 py-10">
        {tab === 0 && (
          <div className="paper-card p-8 space-y-6">
            <Field label="Gelin Adı" value={w.bride_name} onChange={v=>set("bride_name", v)} testid="field-bride" />
            <Field label="Damat Adı" value={w.groom_name} onChange={v=>set("groom_name", v)} testid="field-groom" />
            <div className="flex gap-3 items-end">
              <div className="flex-1"><Field label="URL Slug" value={w.slug} onChange={v=>set("slug", v)} testid="field-slug" /></div>
              <button onClick={autoslug} className="btn-ghost">Otomatik</button>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              <Field label="Düğün Tarihi" type="date" value={w.wedding_date} onChange={v=>set("wedding_date", v)} testid="field-date" />
              <Field label="Düğün Saati" type="time" value={w.wedding_time} onChange={v=>set("wedding_time", v)} testid="field-time" />
            </div>
            <Field label="Mekân" value={w.venue} onChange={v=>set("venue", v)} testid="field-venue" />
            <Field label="Adres" value={w.address} onChange={v=>set("address", v)} testid="field-address" />
            <Field label="Google Maps URL" value={w.maps_url} onChange={v=>set("maps_url", v)} testid="field-maps" />
            <Field label="Davet Mesajı" value={w.invitation_message} onChange={v=>set("invitation_message", v)} testid="field-invmsg" />
            <Field label="Giriş Metni" value={w.intro_text} onChange={v=>set("intro_text", v)} multiline testid="field-intro" />
            <label className="flex items-center gap-3">
              <input type="checkbox" checked={w.published} onChange={e=>set("published", e.target.checked)} data-testid="field-published" />
              <span className="text-sm">Yayında</span>
            </label>
          </div>
        )}

        {tab === 1 && (
          <div className="paper-card p-8 space-y-6">
            <div>
              <div className="eyebrow mb-3">Tema Seçimi</div>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                {THEMES.map(t => (
                  <button key={t.code} onClick={()=>set("theme", t.code)}
                    className={`p-4 border ${w.theme===t.code ? "border-accent-brand" : "border-brand"} paper-card`}
                    style={w.theme===t.code ? {borderColor:"var(--accent)"} : {}}
                    data-testid={`theme-${t.code}`}>
                    <div className="font-serif-elegant text-lg">{t.name}</div>
                  </button>
                ))}
              </div>
            </div>
            <Field label="Kapak Fotoğrafı URL" value={w.cover_image} onChange={v=>set("cover_image", v)} testid="field-cover" />
            <div>
              <div className="eyebrow mb-2">Kapak Fotoğrafı Yükle</div>
              <input type="file" accept="image/*" onChange={async(e)=>{if(e.target.files[0]){const url=await uploadFile(e.target.files[0]); set("cover_image", url); toast.success("Yüklendi");}}} data-testid="field-cover-upload" />
            </div>
            <Field label="Hero Video URL (opsiyonel)" value={w.hero_video} onChange={v=>set("hero_video", v)} testid="field-video" />
            <div className="paper-card p-5 space-y-4">
              <label className="flex items-center gap-3 text-xs tracking-luxury uppercase text-secondary-brand">
                <input type="checkbox" checked={!!w.hero_video_enabled} onChange={e=>set("hero_video_enabled", e.target.checked)} data-testid="field-hero-video-enabled" />
                Tam Ekran Video Hero
              </label>
              <Field label="Video URL (MP4, 9:16 önerilir)" value={w.hero_video_url} onChange={v=>set("hero_video_url", v)} testid="field-hero-video-url" />
              <Field label="Video Poster Görseli URL" value={w.hero_video_poster} onChange={v=>set("hero_video_poster", v)} testid="field-hero-video-poster" />
            </div>
            <label className="block">
              <div className="text-xs tracking-luxury uppercase text-secondary-brand mb-2">Overlay Opaklığı ({w.overlay_opacity})</div>
              <input type="range" min="0" max="0.8" step="0.05" value={w.overlay_opacity}
                onChange={e=>set("overlay_opacity", parseFloat(e.target.value))} className="w-full" data-testid="field-overlay" />
            </label>
          </div>
        )}

        {tab === 2 && (
          <div className="paper-card p-8">
            <div className="flex items-center justify-between mb-4">
              <div className="eyebrow">Galeri Fotoğrafları ({w.gallery.length})</div>
              <div className="flex gap-2">
                <button onClick={addGalleryUrl} className="btn-ghost" data-testid="gallery-add-url">+ URL</button>
                <label className="btn-ghost cursor-pointer" data-testid="gallery-add-file">
                  + Yükle
                  <input type="file" accept="image/*" className="hidden" onChange={async(e)=>{if(e.target.files[0]){await addGalleryFile(e.target.files[0]); toast.success("Yüklendi");}}} />
                </label>
              </div>
            </div>
            <div className="grid grid-cols-3 md:grid-cols-4 gap-3">
              {w.gallery.map((g, i) => (
                <div key={g.id} className="relative group">
                  <img src={g.url} alt="" className="w-full aspect-square object-cover" />
                  <button onClick={()=>set("gallery", w.gallery.filter(x=>x.id!==g.id))}
                    className="absolute top-1 right-1 bg-black/60 text-white w-6 h-6 text-xs" data-testid={`gallery-remove-${i}`}>×</button>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === 3 && (
          <ListEditor title="Bizim Hikâyemiz" items={w.story} onChange={v=>set("story", v)} fields={[
            {key:"year", label:"Yıl"},{key:"title", label:"Başlık"},{key:"description", label:"Açıklama", multiline: true},{key:"image_url", label:"Görsel URL"}
          ]} testidPrefix="story" />
        )}

        {tab === 4 && (
          <div className="space-y-8">
            <ListEditor title="Program" items={w.program} onChange={v=>set("program", v)} fields={[
              {key:"time", label:"Saat"},{key:"title", label:"Başlık"}
            ]} testidPrefix="program" />
            <div className="hairline"></div>
            <div>
              <Field label="Akış Altında Not" multiline value={w.program_note} onChange={v=>set("program_note", v)} testid="field-program-note" />
              <p className="text-xs text-secondary-brand mt-2">Akış bilgilerinin altında gösterilecek ek bilgileri buraya yazabilirsiniz. Her satır ayrı gösterilir.</p>
            </div>
          </div>
        )}

        {tab === 5 && (
          <div className="space-y-6">
            <label className="paper-card p-4 flex items-center justify-between gap-4 cursor-pointer">
              <span className="text-xs tracking-luxury uppercase text-secondary-brand">LCV'yi Ana Sayfada Göster</span>
              <span className="flex items-center gap-2 text-xs uppercase tracking-luxury">
                <span data-testid="rsvp-visible-state">{w.rsvp_visible !== false ? "ON" : "OFF"}</span>
                <input type="checkbox" checked={w.rsvp_visible !== false} onChange={e=>set("rsvp_visible", e.target.checked)} data-testid="field-rsvp-visible" />
              </span>
            </label>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <StatCell label="Toplam" value={rsvps.stats?.total || 0} />
              <StatCell label="Katılacak" value={rsvps.stats?.attending || 0} />
              <StatCell label="Katılamayacak" value={rsvps.stats?.not_attending || 0} />
              <StatCell label="Toplam Kişi" value={rsvps.stats?.total_guests || 0} />
              <button onClick={exportCsv} className="btn-primary" data-testid="rsvp-export">CSV İndir</button>
            </div>
            <div className="flex gap-2 flex-wrap">
              {[["all","Tümü"],["yes","Katılacak"],["no","Katılamayacak"]].map(([k,l]) => (
                <button key={k} onClick={()=>setRsvpFilter(k)} className={`btn-ghost ${rsvpFilter===k?"text-accent-brand":""}`} data-testid={`rsvp-filter-${k}`}>{l}</button>
              ))}
              <input placeholder="İsim ara..." value={rsvpSearch} onChange={e=>setRsvpSearch(e.target.value)}
                className="flex-1 bg-transparent border-b border-brand py-1 outline-none text-sm" data-testid="rsvp-search" />
            </div>
            <div className="paper-card overflow-hidden">
              <table className="w-full text-sm">
                <thead className="text-xs uppercase tracking-luxury text-secondary-brand border-b border-brand">
                  <tr><th className="p-3 text-left">Ad</th><th className="p-3">Katılım</th><th className="p-3">Kişi</th><th className="p-3">Not</th><th className="p-3">Tarih</th></tr>
                </thead>
                <tbody>
                  {filteredRsvps.map(r => (
                    <tr key={r.id} className="border-b border-brand">
                      <td className="p-3">{r.name}</td>
                      <td className="p-3 text-center">{r.attending ? "✓" : "✕"}</td>
                      <td className="p-3 text-center">{r.guest_count}</td>
                      <td className="p-3 text-secondary-brand italic">{r.note}</td>
                      <td className="p-3 text-secondary-brand text-xs">{r.created_at?.slice(0,10)}</td>
                    </tr>
                  ))}
                  {filteredRsvps.length===0 && <tr><td colSpan={5} className="p-6 text-center text-secondary-brand">Kayıt yok.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === 6 && (
          <div className="paper-card p-8 space-y-4">
            <Field label="Müzik URL" value={w.music_url} onChange={v=>set("music_url", v)} testid="field-music-url" />
            <div>
              <div className="eyebrow mb-2">Müzik Dosyası Yükle</div>
              <input type="file" accept="audio/*" onChange={async(e)=>{if(e.target.files[0]){const url=await uploadFile(e.target.files[0]); set("music_url", url); toast.success("Yüklendi");}}} data-testid="field-music-upload" />
            </div>
            {w.music_url && <audio src={w.music_url} controls className="w-full" />}
          </div>
        )}

        {tab === 7 && (
          <div className="space-y-4">
            <div className="flex gap-2">
              {[["pending","Onay Bekleyen"],["approved","Onaylı"],["rejected","Reddedilen"],["all","Tümü"]].map(([k,l])=>(
                <button key={k} onClick={()=>setMemFilter(k)} className={`btn-ghost ${memFilter===k?"text-accent-brand":""}`} data-testid={`mem-filter-${k}`}>{l}</button>
              ))}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {memories.map(m => (
                <div key={m.id} className="paper-card overflow-hidden">
                  {m.media_kind === "video"
                    ? <video src={`${process.env.REACT_APP_BACKEND_URL}${m.url}`} controls className="w-full aspect-square object-cover" />
                    : <img src={`${process.env.REACT_APP_BACKEND_URL}${m.url}`} className="w-full aspect-square object-cover" alt="" />}
                  <div className="p-3 text-xs">
                    <div className="font-semibold">{m.guest_name || "Konuk"}</div>
                    <div className="text-secondary-brand italic">{m.note}</div>
                    <div className="mt-2 flex gap-1">
                      <button onClick={async()=>{await api.patch(`/memories/${m.id}?status=approved`); loadMemories(); toast.success("Onaylandı");}} className="btn-ghost flex-1" data-testid={`mem-approve-${m.id}`}>Onayla</button>
                      <button onClick={async()=>{await api.patch(`/memories/${m.id}?status=rejected`); loadMemories();}} className="btn-ghost flex-1" data-testid={`mem-reject-${m.id}`}>Reddet</button>
                    </div>
                  </div>
                </div>
              ))}
              {memories.length===0 && <div className="col-span-full text-center text-secondary-brand py-10">Anı yok.</div>}
            </div>
          </div>
        )}

        {tab === 8 && (
          <div className="paper-card p-8 text-center">
            <div className="eyebrow mb-4">Davetiye QR Kodu</div>
            <div className="font-serif-elegant text-2xl mb-6 break-all">{publicUrl}</div>
            {qrUrl && <img src={qrUrl} alt="QR" className="mx-auto w-64 h-64 bg-white p-4" data-testid="qr-image" />}
            <div className="mt-6 flex justify-center gap-3">
              <a href={qrUrl} download="qr.png" className="btn-primary" data-testid="qr-download-png">PNG İndir</a>
              <a href={qrSvg} download="qr.svg" className="btn-ghost" data-testid="qr-download-svg">SVG İndir</a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type="text", multiline=false, testid }) {
  return (
    <label className="block">
      <div className="text-xs tracking-luxury uppercase text-secondary-brand mb-2">{label}</div>
      {multiline
        ? <textarea rows={3} value={value||""} onChange={e=>onChange(e.target.value)}
            className="w-full bg-transparent border border-brand p-3 outline-none focus:border-accent-brand resize-none" data-testid={testid} />
        : <input type={type} value={value||""} onChange={e=>onChange(e.target.value)}
            className="w-full bg-transparent border-b border-brand py-2 outline-none focus:border-accent-brand" data-testid={testid} />}
    </label>
  );
}

function StatCell({ label, value }) {
  return <div className="paper-card p-4"><div className="eyebrow text-[0.6rem]">{label}</div><div className="font-serif-elegant text-2xl mt-1">{value}</div></div>;
}

function ListEditor({ title, items, onChange, fields, testidPrefix }) {
  const add = () => onChange([...items, { id: crypto.randomUUID(), ...Object.fromEntries(fields.map(f=>[f.key, ""])) }]);
  const upd = (id, k, v) => onChange(items.map(it => it.id===id ? {...it, [k]:v} : it));
  const rem = (id) => onChange(items.filter(it => it.id !== id));
  const move = (id, dir) => {
    const i = items.findIndex(it => it.id === id);
    const j = i + dir;
    if (j < 0 || j >= items.length) return;
    const copy = [...items];
    [copy[i], copy[j]] = [copy[j], copy[i]];
    onChange(copy);
  };
  return (
    <div className="paper-card p-8">
      <div className="flex justify-between mb-4">
        <div className="eyebrow">{title} ({items.length})</div>
        <button onClick={add} className="btn-ghost" data-testid={`${testidPrefix}-add`}>+ Ekle</button>
      </div>
      <div className="space-y-4">
        {items.map((it, idx) => (
          <div key={it.id} className="border border-brand p-4 space-y-3">
            {fields.map(f => (
              <Field key={f.key} label={f.label} value={it[f.key]} onChange={v=>upd(it.id, f.key, v)} multiline={f.multiline} testid={`${testidPrefix}-${idx}-${f.key}`} />
            ))}
            <div className="flex gap-2 justify-end">
              <button onClick={()=>move(it.id,-1)} className="btn-ghost">↑</button>
              <button onClick={()=>move(it.id,1)} className="btn-ghost">↓</button>
              <button onClick={()=>rem(it.id)} className="btn-ghost" data-testid={`${testidPrefix}-remove-${idx}`}>Sil</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
